import React from 'react';
import { useLocation } from 'react-router-dom';
import HierarchyTree from '../Hierarchy';
import { HierarchySkeleton } from '../Hierarchy';
import { useSidebarSelection } from '../../contexts/SidebarSelectionContext';
import SidebarShell from '../SidebarShell/SidebarShell';
import './DashboardSidebar.css';

export default function DashboardSidebar({
  hierarchy,
  sidebarHierarchy,
  activeExamId,
  showAllDecks,
  toggleShowAllDecks,
  showSidebarLoading,
  domains,
  isHintPending,
  hintsActive,
  onboardingDiscovered,
  onboardingCount,
  onTopicClick,
  onDeleteDeck,
  onMoveDomain,
  onDomainClick,
  onRenameDomain,
  onAddTopicToExam,
  onMergeDomain,
}) {
  const selection = useSidebarSelection();
  const { pathname } = useLocation();
  // Filter toggle is dashboard-only. Everywhere else defaults to showing
  // all decks (full hierarchy), no toggle in the header.
  const isDashboard = pathname === '/dashboard';
  const showFilterToggle = isDashboard && activeExamId;
  const effectiveShowAllDecks = isDashboard ? showAllDecks : true;
  const multiselect = selection?.enabled === true;
  const hasTopicOverride = typeof selection?.topicClickOverride === 'function';
  const hasExamOverride = typeof selection?.examClickOverride === 'function';
  const hasDomainOverride = typeof selection?.domainClickOverride === 'function';
  const selectionActive = multiselect || hasTopicOverride || hasExamOverride || hasDomainOverride;

  const effectiveTopicClick = hasTopicOverride
    ? selection.topicClickOverride
    : multiselect
      ? selection.toggleTopic
      : onTopicClick;
  const effectiveExamClick = hasExamOverride
    ? selection.examClickOverride
    : multiselect
      ? selection.toggleExam
      : null;
  const effectiveDomainToggle = hasDomainOverride
    ? selection.domainClickOverride
    : multiselect
      ? selection.toggleDomain
      : null;
  const effectiveDomainClick = multiselect ? selection.toggleDomain : onDomainClick;
  const headerExtras = showFilterToggle ? (
    <button
      className={`sidebar-filter-toggle ${showAllDecks ? 'active' : ''}`}
      onClick={toggleShowAllDecks}
      aria-label={showAllDecks ? 'Showing all decks' : 'Showing exam decks only'}
    >
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
        {showAllDecks ? (
          <path d="M1 2h14M1 5.5h14M1 9h14M1 12.5h14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        ) : (
          <path d="M1 2h14M3 5.5h10M5 9h6M7 12.5h2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        )}
      </svg>
    </button>
  ) : null;
  return (
    <SidebarShell
      title="Table of Contents"
      className="dashboard-sidebar"
      headerExtras={headerExtras}
    >
      {showSidebarLoading ? (
        <HierarchySkeleton />
      ) : domains.length === 0 ? (
        <div className="sidebar-empty">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="4" y1="6" x2="20" y2="6" />
            <line x1="4" y1="12" x2="20" y2="12" />
            <line x1="4" y1="18" x2="14" y2="18" />
          </svg>
          <p className="sidebar-empty-label">No content yet</p>
          <p className="sidebar-empty-hint">Upload PowerPoints to get started</p>
        </div>
      ) : (
        <div className={`${isHintPending('sidebar_deck') ? 'hint-sidebar-deck-active' : ''}${selectionActive ? ' sidebar-multiselect' : ''}`}>
          <HierarchyTree
            hierarchy={(!activeExamId || effectiveShowAllDecks) ? hierarchy : sidebarHierarchy}
            showCheckboxes={false}
            selectedDomains={selectionActive ? selection.selectedDomains : undefined}
            selectedExams={selectionActive ? selection.selectedExams : undefined}
            selectedTopics={selectionActive ? selection.selectedTopics : undefined}
            onTopicClick={effectiveTopicClick}
            onExamClick={effectiveExamClick}
            onToggleDomain={effectiveDomainToggle || undefined}
            onDeleteDeck={selectionActive ? null : onDeleteDeck}
            onMoveDomain={selectionActive ? null : onMoveDomain}
            onDomainClick={effectiveDomainClick}
            onRenameDomain={selectionActive ? null : onRenameDomain}
            onAddToExam={!selectionActive && activeExamId ? onAddTopicToExam : null}
            onMergeDomain={selectionActive ? null : onMergeDomain}
          />
        </div>
      )}
    </SidebarShell>
  );
}
